import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import classNames from 'classnames';
import styled from 'styled-components';

import { Lightning } from '@sorare/core/src/atoms/icons/Lightning';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { ClubIcon } from '@sorare/core/src/components/collections/ClubIcon';
import { getCollectionsTeamShield } from '@sorare/core/src/lib/collections';

import type { AllStepsProgressBar_cardCollection } from './__generated__/index.graphql.ts';

const DOT_SIZE = 12;
const Root = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 6px;
  margin: var(--triple-unit) var(--double-unit) var(--triple-unit) 0;
  &:before {
    position: absolute;
    z-index: 0;
    top: 0;
    right: ${DOT_SIZE / 2}px;
    bottom: 0;
    left: ${DOT_SIZE / 2}px;
    color: var(--c-nd-50);
    background: currentcolor;
    border-radius: 2em;
    content: '';
  }
`;
const Bar = styled.div`
  position: absolute;
  left: ${DOT_SIZE / 2}px;
  top: 0;
  border-radius: 2em;
  background: linear-gradient(
    to right,
    #ffb800 0%,
    #f0ce1d 37.72%,
    #fcff63 100%
  );
  height: 100%;
  width: 0;
  transition-timing-function: ease-in-out;
  transition-property: width;
`;
const StepContainer = styled.div`
  position: relative;
`;
const Top = styled(Horizontal).attrs({ gap: 0 })`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  bottom: calc(100% + var(--unit));
  &.hidden {
    left: 0;
    visibility: hidden;
  }
`;
const Bottom = styled.div`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  white-space: nowrap;
  top: calc(100% + var(--unit));
  &.hidden {
    left: 0;
    visibility: hidden;
  }
  &.icon {
    top: 100%;
  }
`;
const Step = styled.div`
  position: relative;
  width: ${DOT_SIZE}px;
  height: ${DOT_SIZE}px;
  border-radius: 50%;
  background-color: #383838;
  transition: 0.1s ease background-color;
  &.completed {
    background-color: var(--c-yellow-400);
  }
  &.hidden {
    visibility: hidden;
  }
`;
const LightningWrapper = styled.span`
  width: var(--intermediate-unit);
  display: flex;
  justify-content: center;
`;

const getProgress = (
  score: number,
  thresholds: { threshold: number; bonus: Nullable<number> }[]
) => {
  const stepLength = 100 / (thresholds.length - 1);
  const values = thresholds.map(({ threshold }) => threshold);
  const lastIndex = values.length - 1;
  const closestNextStep =
    score > values[lastIndex] ? lastIndex : values.findIndex(x => score <= x);
  const closestPreviousStep = closestNextStep === 0 ? 0 : closestNextStep - 1;
  const delta = score - values[closestPreviousStep];
  const maxDelta = values[closestNextStep] - values[closestPreviousStep] || 1;

  return Math.min(stepLength * (closestPreviousStep + delta / maxDelta), 100);
};

const insertIconScoreIntoThresholds = (
  thresholds: AllStepsProgressBar_cardCollection['bonusThresholds'],
  value: number
) => {
  let index = 0;
  for (let i = 1; i < thresholds.length; i += 1) {
    if (value > thresholds[i].threshold) {
      index = i + 1;
    }
  }

  return [
    ...thresholds.slice(0, index),
    { threshold: value, bonus: null },
    ...thresholds.slice(index),
  ];
};
type Props = {
  cardCollection: AllStepsProgressBar_cardCollection;
  score: number;
};
export const AllStepsProgressBar = ({ cardCollection, score }: Props) => {
  const {
    score: teamShieldScore,
    name,
    pictureUrl,
  } = getCollectionsTeamShield(cardCollection);

  const thresholdsWithPicture =
    teamShieldScore && pictureUrl
      ? insertIconScoreIntoThresholds(
          cardCollection.bonusThresholds,
          teamShieldScore
        )
      : cardCollection.bonusThresholds;

  const thresholds = [{ threshold: 0, bonus: null }, ...thresholdsWithPicture];

  const progress = getProgress(score, thresholds);
  const completedSteps =
    score === 0
      ? []
      : thresholds.filter(
          (_, i) => (i / (thresholds.length - 1)) * 100 <= progress
        );

  return (
    <Root>
      <Bar
        style={
          {
            width: `calc((100% - ${thresholds.length * DOT_SIZE}px) * ${
              progress / 100
            } + ${Math.max(0, completedSteps.length - 0.5) * DOT_SIZE}px )`,
          } as React.CSSProperties
        }
      />
      {thresholds.map(({ threshold, bonus }, i) => {
        const isLastThreshold = i === thresholds.length - 1;
        const isCompleted = !!completedSteps[i];

        return (
          <StepContainer key={threshold}>
            <Top className={classNames({ hidden: i === 0 })}>
              <LabelS bold>
                {threshold}
                {!!isLastThreshold && '+'}
              </LabelS>
              <LightningWrapper>
                <Lightning />
              </LightningWrapper>
            </Top>
            <Step
              className={classNames({
                completed: isCompleted,
                hidden: i === 0,
              })}
            />
            <Bottom className={classNames({ hidden: i === 0, icon: !bonus })}>
              <LabelS
                bold
                color={isCompleted ? 'var(--c-yellow-400)' : 'var(--c-nd-600)'}
              >
                {bonus ? (
                  `+${bonus * 100}%`
                ) : (
                  <ClubIcon
                    clubIconUrl={pictureUrl || ''}
                    isCompleted={isCompleted}
                    alt={name || ''}
                  />
                )}
              </LabelS>
            </Bottom>
          </StepContainer>
        );
      })}
    </Root>
  );
};

AllStepsProgressBar.fragments = {
  cardCollection: gql`
    fragment AllStepsProgressBar_cardCollection on CardCollection {
      slug
      bonusThresholds {
        threshold
        bonus
      }

      ...getCollectionsShield_cardCollection
    }
    ${getCollectionsTeamShield.fragments.cardCollection}
  ` as TypedDocumentNode<AllStepsProgressBar_cardCollection>,
};
